// Versiyon: 1.0.0 - Ortak İstatistik Kartı Bileşeni
// Tüm ön muhasebe sayfalarında kullanılan dashboard tarzı özet kartları
import React from 'react';
import { Box, Typography, Paper, Grid, Skeleton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

// Dashboard tarzı kart stilleri
export const StatsCardWrapper = styled(Paper)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
    background: `linear-gradient(to right, white, ${bgcolor}10)`,
  },
}));

export const IconBox = styled(Box)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgcolor,
  marginRight: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    color: 'white',
    fontSize: 24,
  },
}));

export const StatsText = styled(Box)({
  flexGrow: 1,
  '& .MuiTypography-h6': {
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  '& .MuiTypography-body2': {
    fontSize: '0.75rem',
  },
});

// Tek bir istatistik kartı bileşeni
export interface StatsCardItemProps {
  icon: React.ReactNode;
  color: string;
  title: string;
  subtitle: string;
  onClick?: () => void;
}

export const StatsCardItem: React.FC<StatsCardItemProps> = ({ icon, color, title, subtitle, onClick }) => (
  <Tooltip title={title} arrow placement="top" enterDelay={300}>
    <StatsCardWrapper bgcolor={color} onClick={onClick}>
      <IconBox bgcolor={color}>
        {icon}
      </IconBox>
      <StatsText sx={{ overflow: 'hidden' }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: title.length > 15 ? '1.1rem' : title.length > 10 ? '1.3rem' : '1.5rem',
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {subtitle}
        </Typography>
      </StatsText>
    </StatsCardWrapper>
  </Tooltip>
);

// Loading durumunda skeleton kartı
export const StatsCardSkeleton: React.FC = () => (
  <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', borderRadius: 3 }}>
    <Skeleton variant="rounded" width={40} height={40} sx={{ mr: 1, borderRadius: 1 }} />
    <Box sx={{ flexGrow: 1 }}>
      <Skeleton variant="text" width="60%" height={32} />
      <Skeleton variant="text" width="80%" height={16} />
    </Box>
  </Paper>
);

// İstatistik kartları grid container bileşeni
export interface StatsCardsContainerProps {
  children: React.ReactNode;
  loading?: boolean;
  cardCount?: number; // Loading durumunda kaç skeleton gösterilecek
}

export const StatsCardsContainer: React.FC<StatsCardsContainerProps> = ({ children, loading, cardCount = 4 }) => {
  if (loading) {
    return (
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {Array.from({ length: cardCount }).map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={`skeleton-${i}`}>
            <StatsCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} sx={{ mb: 2 }}>
      {children}
    </Grid>
  );
};

// Sık kullanılan tema renkleri
export const STATS_COLORS = {
  green: '#4CAF50',
  red: '#F44336',
  blue: '#2196F3',
  orange: '#FF9800',
  purple: '#9C27B0',
  teal: '#009688',
  grey: '#9E9E9E',
};

// Para birimini formatla (TL işareti sonda)
export const formatStatsCurrency = (amount: number, currency: string = 'TRY'): string => {
  const formatted = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  const symbol = currency === 'TRY' ? '₺' : (currency === 'USD' ? '$' : (currency === 'EUR' ? '€' : currency));
  return `${formatted} ${symbol}`;
};

export default StatsCardItem;
